import AuthContext from '../context/AuthContext';
import { canAccessWidget } from '../widgets/permissions';
import { CLUSTER_WAVEFORM } from '../widgets/clusterWaveform';
import { SPIKE_WAVEFORM_GRID } from '../widgets/spikeWaveformGrid';
import React, { useState, useRef, useEffect } from 'react';
import { translate, useI18n } from '../i18n';
import './WidgetBank.css';
import { SOSSA_WIDGETS } from '../widgets/sossaWidgets';

// Widget definitions with icons and metadata
const WIDGET_DEFINITIONS = {
  [CLUSTER_WAVEFORM.id]: CLUSTER_WAVEFORM,
  [SPIKE_WAVEFORM_GRID.id]: SPIKE_WAVEFORM_GRID,
  ...Object.fromEntries(SOSSA_WIDGETS.map((widget) => [widget.id, widget])),
  clusterList: {
    id: 'clusterList',
    name: translate('widgets.clusterList.name'),
    description: translate('widgets.clusterList.description'),
    icon: '📋',
    category: 'data',
    defaultSize: { width: 760, height: 430 }
  },
  spikeList: {
    id: 'spikeList',
    name: translate('widgets.spikeList.name'),
    description: translate('widgets.spikeList.description'),
    icon: '⚡',
    category: 'data',
    defaultSize: { width: 200, height: 350 }
  },
  clusterStats: {
    id: 'clusterStats',
    name: translate('widgets.clusterStats.name'),
    description: translate('widgets.clusterStats.description'),
    icon: '📊',
    category: 'analysis',
    defaultSize: { width: 200, height: 350 }
  },
  signalView: {
    id: 'signalView',
    name: translate('widgets.signalView.name'),
    description: translate('widgets.signalView.description'),
    icon: '📈',
    category: 'visualization',
    defaultSize: { width: 600, height: 350 }
  },
  dimReduction: {
    id: 'dimReduction',
    name: translate('widgets.dimReduction.name'),
    description: translate('widgets.dimReduction.description'),
    icon: '🎯',
    category: 'visualization',
    defaultSize: { width: 500, height: 400 }
  },
  waveform: {
    id: 'waveform',
    name: translate('widgets.waveform.name'),
    description: translate('widgets.waveform.description'),
    icon: '〰️',
    category: 'visualization',
    defaultSize: { width: 500, height: 400 }
  },
  amplitudeProfile: {
    id: 'amplitudeProfile',
    name: translate('widgets.amplitudeProfile.name'),
    description: translate('widgets.amplitudeProfile.description'),
    icon: 'A',
    category: 'visualization',
    defaultSize: { width: 620, height: 430 }
  },
  clusterComparison: {
    id: 'clusterComparison',
    name: translate('widgets.clusterComparison.name'),
    description: translate('widgets.clusterComparison.description'),
    icon: 'C',
    category: 'analysis',
    defaultSize: { width: 820, height: 500 }
  },
  curator: {
    id: 'curator',
    name: translate('widgets.curator.name'),
    description: translate('widgets.curator.description'),
    icon: 'R',
    category: 'analysis',
    defaultSize: { width: 760, height: 480 }
  },
  rasterPlot: {
    id: 'rasterPlot',
    name: translate('widgets.rasterPlot.name'),
    description: translate('widgets.rasterPlot.description'),
    icon: '|',
    category: 'visualization',
    defaultSize: { width: 760, height: 420 }
  },
  correlogram: {
    id: 'correlogram',
    name: translate('widgets.correlogram.name'),
    description: translate('widgets.correlogram.description'),
    icon: '▦',
    category: 'analysis',
    defaultSize: { width: 820, height: 620 }
  },
  isiHistogram: {
    id: 'isiHistogram',
    name: translate('widgets.isiHistogram.name'),
    description: translate('widgets.isiHistogram.description'),
    icon: 'Ι',
    category: 'analysis',
    defaultSize: { width: 700, height: 430 }
  },
  amplitudeTime: {
    id: 'amplitudeTime',
    name: translate('widgets.amplitudeTime.name'),
    description: translate('widgets.amplitudeTime.description'),
    icon: '↘',
    category: 'visualization',
    defaultSize: { width: 760, height: 440 }
  },
  firingRateTimeline: {
    id: 'firingRateTimeline',
    name: 'Firing Rate Timeline',
    description: 'Inspect cluster activity and stability across recording time',
    icon: '⌁',
    category: 'analysis',
    defaultSize: { width: 760, height: 440 }
  },
  similarityTable: {
    id: 'similarityTable',
    name: 'Similarity Table',
    description: 'Rank and review clusters similar to the primary cluster',
    icon: '≈',
    category: 'analysis',
    defaultSize: { width: 760, height: 480 }
  },
  probeMap: {
    id: 'probeMap',
    name: 'Probe Map',
    description: 'Inspect physical channel geometry and selected-cluster footprints',
    icon: '⌇',
    category: 'visualization',
    defaultSize: { width: 340, height: 680 }
  },
  traceHeatmap: {
    id: 'traceHeatmap',
    name: 'Trace Heatmap',
    description: 'Downsampled all-channel trace image with linked time navigation',
    icon: '▥',
    category: 'visualization',
    defaultSize: { width: 820, height: 520 }
  },
  featureMatrix: {
    id: 'featureMatrix',
    name: 'Feature Matrix',
    description: 'Compare retained spike features and lasso exact spikes for curation',
    icon: '▩',
    category: 'analysis',
    defaultSize: { width: 860, height: 680 }
  },
  templateFeaturePair: {
    id: 'templateFeaturePair',
    name: 'Template Feature Pair',
    description: 'Review pairwise separation for exactly two linked clusters',
    icon: '⋈',
    category: 'analysis',
    defaultSize: { width: 720, height: 500 }
  },
  templateGallery: {
    id: 'templateGallery',
    name: 'Template Gallery',
    description: 'Browse ordered retained templates or deterministic mean waveforms',
    icon: '〽',
    category: 'visualization',
    defaultSize: { width: 840, height: 620 }
  },
  clusterMetricScatter: {
    id: 'clusterMetricScatter',
    name: 'Cluster Metric Scatter',
    description: 'Compare the visible cluster population across configurable quality metrics',
    icon: '⠿',
    category: 'analysis',
    defaultSize: { width: 740, height: 500 }
  },
  spikeAttributeExplorer: {
    id: 'spikeAttributeExplorer',
    name: 'Spike Attribute Explorer',
    description: 'Discover and plot compatible retained scalar or two-dimensional spike attributes',
    icon: '◇',
    category: 'analysis',
    defaultSize: { width: 760, height: 500 }
  },
  analysisWorkspace: {
    id: 'analysisWorkspace',
    name: 'Analysis Workspace',
    description: 'Inspect and export read-only session, wiring, selection, and provenance state',
    icon: '{ }',
    category: 'analysis',
    defaultSize: { width: 820, height: 600 }
  },
  curationActions: {
    id: 'curationActions',
    name: 'Curation Actions',
    description: 'Merge, split, undo, redo, and export non-destructive curated assignments',
    icon: '✂',
    category: 'analysis',
    defaultSize: { width: 760, height: 460 }
  }
};

const CATEGORIES = {
  data: { name: translate('widgets.categories.data.name'), icon: '📁' },
  analysis: { name: translate('widgets.categories.analysis.name'), icon: '🔬' },
  visualization: { name: translate('widgets.categories.visualization.name'), icon: '👁️' }
};

const WidgetBank = ({ 
  isOpen, 
  onClose, 
  widgetStates, 
  onAddWidget,
  onToggleWidget,
  sossaEnabled = false
}) => {
  const { t } = useI18n();
  const auth = React.useContext(AuthContext);
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const bankRef = useRef(null);

  // Filter widgets based on search and category
  const filteredWidgets = Object.values(WIDGET_DEFINITIONS).filter(widget => {
    const matchesSearch = widget.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          widget.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || widget.category === selectedCategory;
    return canAccessWidget(widget.id, auth?.user) && matchesSearch && matchesCategory;
  });

  // Group widgets by category
  const groupedWidgets = filteredWidgets.reduce((acc, widget) => {
    const category = widget.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(widget);
    return acc;
  }, {});

  // Handle drag start
  const handleDragStart = (e, widget) => {
    setDraggedWidget(widget);
    e.dataTransfer.setData('application/json', JSON.stringify(widget));
    e.dataTransfer.effectAllowed = 'copy';
    
    // Create custom drag image
    const dragImage = document.createElement('div');
    dragImage.className = 'widget-drag-preview';
    dragImage.innerHTML = `
      <span class="drag-icon">${widget.icon}</span>
      <span class="drag-name">${widget.name}</span>
    `;
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 50, 25);
    
    // Cleanup drag image after a short delay
    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  };

  const handleDragEnd = () => {
    setDraggedWidget(null);
  };

  const getWidgetActiveCount = (widgetId) => {
    return Object.entries(widgetStates || {}).filter(([instanceId, state]) => (
      state?.visible && (state.type || instanceId.split('__')[0]) === widgetId
    )).length;
  };

  const handleWidgetClick = (widget) => {
    onAddWidget(widget);
    onClose();
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bankRef.current && !bankRef.current.contains(e.target) && 
          !e.target.closest('.widget-bank-floating-toggle')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="widget-bank-overlay">
      <div className="widget-bank" id="widget-bank" ref={bankRef}>
        <div className="widget-bank-header">
          <h3>
            <span className="header-icon">🧩</span>
            {t('widgets.bank.title')}
          </h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="widget-bank-search">
          <input
            type="text"
            placeholder={t('widgets.bank.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="widget-bank-categories">
          <button
            className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            {t('widgets.bank.all')}
          </button>
          {Object.entries(CATEGORIES).map(([key, cat]) => (
            <button
              key={key}
              className={`category-btn ${selectedCategory === key ? 'active' : ''}`}
              onClick={() => setSelectedCategory(key)}
            >
              <span className="cat-icon">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </div>

        <div className="widget-bank-content">
          <p className="drag-hint">
            <span className="hint-icon">💡</span>
            {t('widgets.bank.dragHint')}
          </p>

          {Object.entries(groupedWidgets).map(([category, widgets]) => (
            <div key={category} className="widget-category-group">
              <h4 className="category-title">
                <span>{CATEGORIES[category]?.icon}</span>
                {CATEGORIES[category]?.name || category}
              </h4>
              <div className="widget-items">
                {widgets.map(widget => {
                  const activeCount = getWidgetActiveCount(widget.id);
                  const visible = activeCount > 0;
                  return (
                    <div
                      key={widget.id}
                      className={`widget-bank-item ${visible ? 'active' : ''} ${draggedWidget?.id === widget.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, widget)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleWidgetClick(widget)}
                    >
                      <div className="widget-item-icon">{widget.icon}</div>
                      <div className="widget-item-info">
                        <span className="widget-item-name">{widget.name}</span>
                        <span className="widget-item-desc">{widget.description}</span>
                        {widget.algorithm === 'sossa' && !sossaEnabled && (
                          <span className="widget-item-desc">Requires a SOSSA curator upload</span>
                        )}
                      </div>
                      <div className={`widget-status ${visible ? 'visible' : 'hidden'}`}>
                        {visible ? '✓' : '+'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredWidgets.length === 0 && (
            <div className="no-results">
              <span className="no-results-icon">🔎</span>
              <p>{t('widgets.bank.noResults')}</p>
            </div>
          )}
        </div>

        <div className="widget-bank-footer">
          <span className="widget-count">
            {t('widgets.bank.activeCount', {
              active: Object.values(widgetStates).filter(w => w.visible).length,
              total: Object.keys(WIDGET_DEFINITIONS).length
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

// Export widget definitions for use in other components
export { WIDGET_DEFINITIONS, CATEGORIES };
export default WidgetBank;
